import React, { useState } from 'react';
import { Box, Button, Grid, IconButton, Stack, Typography } from '@mui/material';
import { CheckCircle, CloudUpload, DeleteForeverRounded } from '@mui/icons-material';

const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const FileUpload = ({ maxSize = DEFAULT_MAX_FILE_SIZE, onUpload, supportedFormats }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const handleFileUpload = (uploadedFile) => {
    if (!uploadedFile) return;

    if (uploadedFile.size > maxSize) {
      setError('File exceeds max size (5MB).');
      setFile(null);
      return;
    }

    if (!supportedFormats.some((format) => uploadedFile.name.endsWith(format))) {
      setError(
        `File format not supported. Please upload files with the following extensions: ${supportedFormats.join(', ')}.`,
      );
      setFile(null);
      return;
    }
    setError(null);
    setFile(uploadedFile);
    onUpload(uploadedFile);
  };

  const onDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    handleFileUpload(droppedFile);
  };

  const handleClearFile = () => {
    setFile(null);
    setError(null);
    onUpload(null);
  };

  return (
    <div>
      {!file ? (
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          style={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            textAlign: 'center',
            padding: '20px',
            backgroundColor: '#fafafa',
          }}
        >
          <CloudUpload fontSize="large" color="action" />
          <Typography variant="body2" mt={1}>
            Drag & Drop files here
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Supported formats: {supportedFormats.join(', ')} (Max size: {maxSize / (1024 * 1024)}MB)
          </Typography>

          <Box mt={2}>
            <input
              type="file"
              accept={supportedFormats.join(',')}
              style={{ display: 'none' }}
              id="file-input"
              onChange={(e) => handleFileUpload(e.target.files?.[0])}
            />
            <label htmlFor="file-input">
              <Button variant="contained" color="primary" component="span" size="small">
                Browse
              </Button>
            </label>
          </Box>
        </Box>
      ) : (
        <Grid
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          p={2}
          style={{
            border: '2px solid #4caf50',
            borderRadius: '8px',
            backgroundColor: '#e8f5e9',
          }}
        >
          <Stack>
            <Typography variant="body2" style={{ fontWeight: 'bold', wordBreak: 'break-word', whiteSpace: 'normal' }}>
              {file.name}
            </Typography>
            <Typography
              variant="body2"
              style={{
                color: '#4caf50',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              File successfully uploaded
              <CheckCircle color="success" />
            </Typography>
          </Stack>
          <IconButton onClick={handleClearFile}>
            <DeleteForeverRounded color="error" />
          </IconButton>
        </Grid>
      )}
      {error && (
        <Typography variant="body2" mt={2} color="error">
          {error}
        </Typography>
      )}
    </div>
  );
};

export default FileUpload;
